import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import api from '../../lib/api'

interface DashboardStats {
  totalUsers: number
  activeUsersToday: number
  totalPosts: number
  postsToday: number
  pendingReports: number
  pendingModerationQueue: number
  newUsersThisWeek: number
  contentBlockedToday: number
}

interface RecentActivity {
  id: string
  type: 'report' | 'moderation' | 'user_action' | 'system'
  description: string
  timestamp: string
  severity?: 'low' | 'medium' | 'high' | 'critical'
}

export default function AdminDashboardPage() {
  const { user } = useAuth()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, activityRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/activity/recent'),
        ])
        setStats(statsRes.data)
        setRecentActivity(activityRes.data.activities || [])
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
        // Set mock data for development
        setStats({
          totalUsers: 15420,
          activeUsersToday: 2341,
          totalPosts: 89234,
          postsToday: 1567,
          pendingReports: 23,
          pendingModerationQueue: 45,
          newUsersThisWeek: 892,
          contentBlockedToday: 12,
        })
        setRecentActivity([
          { id: '1', type: 'report', description: 'New report on post by @user123', timestamp: '2 min ago', severity: 'medium' },
          { id: '2', type: 'moderation', description: 'Content blocked by auto-moderation', timestamp: '5 min ago', severity: 'high' },
          { id: '3', type: 'user_action', description: 'User @toxic_user suspended', timestamp: '15 min ago', severity: 'high' },
          { id: '4', type: 'system', description: 'System health check passed', timestamp: '30 min ago', severity: 'low' },
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm">Welcome back, {user?.display_name || 'Admin'}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition">
              Back to App
            </Link>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers.toLocaleString() || '0'}
            subtitle={`+${stats?.newUsersThisWeek || 0} this week`}
            icon="users"
            color="blue"
          />
          <StatCard
            title="Active Today"
            value={stats?.activeUsersToday.toLocaleString() || '0'}
            subtitle={`${((stats?.activeUsersToday || 0) / (stats?.totalUsers || 1) * 100).toFixed(1)}% of total`}
            icon="activity"
            color="green"
          />
          <StatCard
            title="Pending Reports"
            value={stats?.pendingReports.toString() || '0'}
            subtitle={`${stats?.pendingModerationQueue || 0} in moderation queue`}
            icon="flag"
            color="yellow"
            urgent={Boolean(stats?.pendingReports && stats.pendingReports > 10)}
          />
          <StatCard
            title="Content Blocked"
            value={stats?.contentBlockedToday.toString() || '0'}
            subtitle="Today by auto-moderation"
            icon="shield"
            color="red"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <QuickActionButton
                to="/admin/moderation"
                label="Moderation Queue"
                count={stats?.pendingModerationQueue}
                icon="shield"
                urgent
              />
              <QuickActionButton
                to="/admin/reports"
                label="User Reports"
                count={stats?.pendingReports}
                icon="flag"
              />
              <QuickActionButton
                to="/admin/users"
                label="User Management"
                icon="users"
              />
              <QuickActionButton
                to="/admin/analytics"
                label="Analytics"
                icon="chart"
              />
              <QuickActionButton
                to="/admin/settings"
                label="Admin Settings"
                icon="settings"
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Recent Activity</h2>
              <Link to="/admin/activity" className="text-sm text-pink-400 hover:text-pink-300">
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>
          </div>
        </div>

        {/* Safety Overview */}
        <div className="mt-6 bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold mb-4">Youth Safety Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <SafetyMetric
              label="Auto-Moderation Rate"
              value="99.2%"
              description="Content automatically reviewed"
              trend="up"
            />
            <SafetyMetric
              label="Block Rate"
              value="0.3%"
              description="Content blocked by filters"
              trend="stable"
            />
            <SafetyMetric
              label="Report Resolution"
              value="94%"
              description="Reports resolved within 24h"
              trend="up"
            />
            <SafetyMetric
              label="False Positive Rate"
              value="2.1%"
              description="Incorrectly blocked content"
              trend="down"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string
  subtitle: string
  icon: string
  color: 'blue' | 'green' | 'yellow' | 'red'
  urgent?: boolean
}

function StatCard({ title, value, subtitle, icon, color, urgent }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400',
  }

  return (
    <div className={`bg-gray-800 rounded-xl p-6 border ${urgent ? 'border-red-500 animate-pulse' : 'border-gray-700'}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 text-sm">{title}</span>
        <span className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <IconComponent name={icon} />
        </span>
      </div>
      <p className="text-3xl font-bold mb-1">{value}</p>
      <p className="text-gray-500 text-sm">{subtitle}</p>
    </div>
  )
}

interface QuickActionButtonProps {
  to: string
  label: string
  icon: string
  count?: number
  urgent?: boolean
}

function QuickActionButton({ to, label, icon, count, urgent }: QuickActionButtonProps) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between p-3 rounded-lg transition ${
        urgent && count ? 'bg-red-500/20 hover:bg-red-500/30' : 'bg-gray-700/50 hover:bg-gray-700'
      }`}
    >
      <div className="flex items-center gap-3">
        <IconComponent name={icon} />
        <span>{label}</span>
      </div>
      {count !== undefined && (
        <span className={`px-2 py-0.5 rounded text-sm ${urgent && count ? 'bg-red-500 text-white' : 'bg-gray-600'}`}>
          {count}
        </span>
      )}
    </Link>
  )
}

interface ActivityItemProps {
  activity: RecentActivity
}

function ActivityItem({ activity }: ActivityItemProps) {
  const severityColors = {
    low: 'text-gray-400',
    medium: 'text-yellow-400',
    high: 'text-orange-400',
    critical: 'text-red-400',
  }

  const typeIcons = {
    report: 'flag',
    moderation: 'shield',
    user_action: 'user',
    system: 'settings',
  }

  return (
    <div className="flex items-center gap-4 p-3 bg-gray-700/30 rounded-lg">
      <span className={severityColors[activity.severity || 'low']}>
        <IconComponent name={typeIcons[activity.type]} />
      </span>
      <div className="flex-1">
        <p className="text-sm">{activity.description}</p>
        <p className="text-xs text-gray-500">{activity.timestamp}</p>
      </div>
      {activity.severity && activity.severity !== 'low' && (
        <span className={`text-xs px-2 py-0.5 rounded ${severityColors[activity.severity]} bg-gray-700`}>
          {activity.severity}
        </span>
      )}
    </div>
  )
}

interface SafetyMetricProps {
  label: string
  value: string
  description: string
  trend: 'up' | 'down' | 'stable'
}

function SafetyMetric({ label, value, description, trend }: SafetyMetricProps) {
  const trendIcons = {
    up: '↑',
    down: '↓',
    stable: '→',
  }

  const trendColors = {
    up: 'text-green-400',
    down: 'text-red-400',
    stable: 'text-gray-400',
  }

  return (
    <div className="text-center">
      <p className="text-3xl font-bold mb-1">
        {value}
        <span className={`text-lg ml-1 ${trendColors[trend]}`}>{trendIcons[trend]}</span>
      </p>
      <p className="font-medium">{label}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  )
}

function IconComponent({ name }: { name: string }) {
  // Simple icon implementation - in production, use a proper icon library
  const icons: Record<string, string> = {
    users: '👥',
    activity: '📊',
    flag: '🚩',
    shield: '🛡️',
    chart: '📈',
    settings: '⚙️',
    user: '👤',
  }

  return <span className="text-lg">{icons[name] || '📌'}</span>
}
